import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ACADEMY_LOGO } from "../data/assets";
import { Lock, Loader2, CheckCircle2, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG_IMAGE = "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      toast.success("Password updated. You can now sign in.");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-black/40 border border-white/15 rounded-lg pl-11 pr-11 py-3.5 text-white text-sm placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24] focus-visible:border-transparent transition-all";

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#0a0a0a] text-white flex items-center justify-center px-4 py-10">
      <div className="absolute inset-0 overflow-hidden">
        <div className="kenburns-bg" style={{ backgroundImage: `url(${BG_IMAGE})` }} />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 15%, rgba(196,30,36,0.20), transparent 55%)" }} />

      <div className="relative z-10 w-full max-w-md login-rise" data-testid="reset-page">
        <div className="glass-card rounded-2xl p-7 sm:p-9 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
          <div className="flex flex-col items-center text-center mb-6">
            <img src={ACADEMY_LOGO} alt="The Next O Technical Academy" className="w-44 h-auto object-contain mb-3" />
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#C41E24]">Next O Soccer</div>
            <h1 className="font-headings text-3xl sm:text-4xl uppercase tracking-wide leading-none mt-1">New Password</h1>
            <p className="text-sm text-white/50 mt-2">Choose a strong new password for your account.</p>
          </div>

          {!token ? (
            <div data-testid="reset-invalid" className="text-center text-sm text-white/60 py-4">
              This reset link is missing or invalid. Please request a new one from the login page.
              <button onClick={() => navigate("/login")} className="block mx-auto mt-5 text-xs font-bold uppercase tracking-wider text-[#C41E24] hover:text-white transition-colors">Back to sign in</button>
            </div>
          ) : done ? (
            <div data-testid="reset-done" className="text-center py-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#22C55E]/15 border border-[#22C55E]/40 flex items-center justify-center mb-4">
                <CheckCircle2 size={26} className="text-[#22C55E]" />
              </div>
              <p className="text-sm text-white/70">Your password has been updated. Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-form" className="space-y-4">
              <div className="relative">
                <Lock size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
                <input data-testid="reset-password" type={showPw ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="New password" className={inputCls} />
                <button type="button" onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/35 hover:text-white/80 transition-colors" aria-label="Toggle password">
                  {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              <div className="relative">
                <Lock size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
                <input data-testid="reset-confirm" type={showPw ? "text" : "password"} required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className={inputCls} />
              </div>
              {error && <div data-testid="reset-error" className="text-sm text-[#ff6b6b] bg-[#C41E24]/10 border border-[#C41E24]/30 rounded-lg px-3 py-2.5">{error}</div>}
              <button data-testid="reset-submit" type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-sm font-bold uppercase tracking-widest py-3.5 rounded-lg transition-all disabled:opacity-60 shadow-[0_10px_30px_rgba(196,30,36,0.4)]">
                {loading ? <Loader2 size={17} className="animate-spin" /> : <CheckCircle2 size={16} />}
                {loading ? "Updating…" : "Set New Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
